// ==================== Core Types ====================

export type CalendarView = 'day' | 'week' | 'month' | 'resource-day' | 'resource-week'

export type ResourceGrouping = 'by-resource' | 'by-date'

export type AppointmentStatus =
  | 'SCHEDULED'
  | 'CONFIRMED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'NO_SHOW'

export type AppointmentType =
  | 'INITIAL_CONSULTATION'
  | 'FOLLOW_UP'
  | 'RESULTS_REVIEW'
  | 'PRESCRIPTION_RENEWAL'

// ==================== Data Models ====================

export interface CalendarDoctor {
  id: string
  name: string
  title?: string
  specialty?: string
}

export interface CalendarPatient {
  id: string
  name: string
}

export interface CalendarResource {
  id: string
  name: string
  title?: string
  color?: string
  specialty?: string
}

export interface CalendarEvent {
  id: string
  title: string
  start: Date | string
  end: Date | string
  duration: number // minutes
  status: AppointmentStatus
  type: AppointmentType
  doctor: CalendarDoctor
  patient: CalendarPatient
  notes?: string
  roomUrl?: string
}

// ==================== Status Configuration ====================

export interface StatusConfig {
  label: string
  labelSr: string // Serbian
  bgColor: string
  borderColor: string
  textColor: string
}

export const STATUS_CONFIG: Record<AppointmentStatus, StatusConfig> = {
  SCHEDULED: {
    label: 'Scheduled',
    labelSr: 'Zakazano',
    bgColor: '#f3f4f6',
    borderColor: '#9ca3af',
    textColor: '#4b5563',
  },
  CONFIRMED: {
    label: 'Confirmed',
    labelSr: 'Potvrđeno',
    bgColor: '#dcfce7',
    borderColor: '#16a34a',
    textColor: '#15803d',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    labelSr: 'U toku',
    bgColor: '#fce7f3',
    borderColor: '#ec4899',
    textColor: '#be185d',
  },
  COMPLETED: {
    label: 'Completed',
    labelSr: 'Završeno',
    bgColor: '#d1fae5',
    borderColor: '#10b981',
    textColor: '#047857',
  },
  CANCELLED: {
    label: 'Cancelled',
    labelSr: 'Otkazano',
    bgColor: '#fee2e2',
    borderColor: '#ef4444',
    textColor: '#dc2626',
  },
  NO_SHOW: {
    label: 'No Show',
    labelSr: 'Propušteno',
    bgColor: '#fef3c7',
    borderColor: '#f97316',
    textColor: '#c2410c',
  },
}

// ==================== Type Labels ====================

export const TYPE_LABELS: Record<AppointmentType, { label: string; labelSr: string }> = {
  INITIAL_CONSULTATION: {
    label: 'Initial Consultation',
    labelSr: 'Prva konsultacija',
  },
  FOLLOW_UP: {
    label: 'Follow-up',
    labelSr: 'Kontrola',
  },
  RESULTS_REVIEW: {
    label: 'Results Review',
    labelSr: 'Pregled rezultata',
  },
  PRESCRIPTION_RENEWAL: {
    label: 'Prescription Renewal',
    labelSr: 'Obnova recepta',
  },
}

// ==================== Component Props ====================

export interface CalendarProps {
  /** Events to display */
  events: CalendarEvent[]
  /** Available doctors for filtering */
  doctors?: CalendarDoctor[]
  /** Resources for resource views (doctors, rooms, etc.) */
  resources?: CalendarResource[]
  /** Resource grouping mode for resource-week view */
  resourceGrouping?: ResourceGrouping
  /** Initial view */
  defaultView?: CalendarView
  /** Initial date */
  defaultDate?: Date
  /** Selected doctor ID for filtering (null = all) */
  selectedDoctorId?: string | null
  /** Enable drag and drop */
  editable?: boolean
  /** Enable multi-select events */
  multiSelect?: boolean
  /** Slot duration in minutes */
  slotDuration?: number
  /** Show slot duration picker in header */
  showSlotDurationPicker?: boolean
  /** Start hour of day (0-23) */
  startHour?: number
  /** End hour of day (0-23) */
  endHour?: number
  /** Locale */
  locale?: 'sr' | 'en'
  /** Loading state */
  loading?: boolean
  /** Callbacks */
  onEventClick?: (event: CalendarEvent) => void
  onSlotClick?: (date: Date, hour: number, minute: number, resourceId?: string) => void
  onEventDrop?: (event: CalendarEvent, newStart: Date, resourceId?: string) => void
  onEventResize?: (event: CalendarEvent, newDuration: number) => void
  onViewChange?: (view: CalendarView) => void
  onDateChange?: (date: Date) => void
  onDoctorFilterChange?: (doctorId: string | null) => void
  onSelectionChange?: (selectedIds: string[]) => void
  onSlotDurationChange?: (duration: number) => void
}

export interface DayViewProps {
  date: Date
  events: CalendarEvent[]
  slotDuration: number
  startHour: number
  endHour: number
  editable: boolean
  locale: 'sr' | 'en'
  selectedEventIds?: string[]
  onEventClick?: (event: CalendarEvent, modifiers?: { ctrl?: boolean; shift?: boolean }) => void
  onSlotClick?: (date: Date, hour: number, minute: number) => void
  onEventDrop?: (event: CalendarEvent, newStart: Date) => void
}

export interface WeekViewProps extends Omit<DayViewProps, 'date'> {
  weekStart: Date
}

export interface MonthViewProps {
  month: Date
  events: CalendarEvent[]
  locale: 'sr' | 'en'
  onEventClick?: (event: CalendarEvent) => void
  onDayClick?: (date: Date) => void
}

export interface AppointmentCardProps {
  event: CalendarEvent
  locale: 'sr' | 'en'
  compact?: boolean
  draggable?: boolean
  resizable?: boolean
  selected?: boolean
  onClick?: () => void
  onDragStart?: (e: React.DragEvent) => void
  onResizeStart?: (e: React.MouseEvent) => void
}

// ==================== Resource View Props ====================

export interface ResourceDayViewProps {
  date: Date
  events: CalendarEvent[]
  resources: CalendarResource[]
  slotDuration: number
  startHour: number
  endHour: number
  editable: boolean
  locale: 'sr' | 'en'
  selectedEventIds?: string[]
  onEventClick?: (event: CalendarEvent, modifiers?: { ctrl?: boolean; shift?: boolean }) => void
  onSlotClick?: (date: Date, hour: number, minute: number, resourceId: string) => void
  onEventDrop?: (event: CalendarEvent, newStart: Date, resourceId?: string) => void
}

export interface ResourceWeekViewProps {
  weekStart: Date
  events: CalendarEvent[]
  resources: CalendarResource[]
  grouping: ResourceGrouping
  slotDuration: number
  startHour: number
  endHour: number
  editable: boolean
  locale: 'sr' | 'en'
  selectedEventIds?: string[]
  onEventClick?: (event: CalendarEvent, modifiers?: { ctrl?: boolean; shift?: boolean }) => void
  onSlotClick?: (
    date: Date,
    hour: number,
    minute: number,
    resourceId: string
  ) => void
  onEventDrop?: (event: CalendarEvent, newStart: Date, resourceId?: string) => void
}

// ==================== Time Slot ====================

export interface TimeSlot {
  hour: number
  minute: number
  label: string
}

// ==================== Event Layout (for overlap handling) ====================

export interface EventLayout {
  event: CalendarEvent
  column: number
  totalColumns: number
  top: number
  height: number
}
